import React, { useEffect, useState } from "react";
import AdminPageHeader from "../../components/AdminPageHeader";
import useUserRole, { isSuperRole } from "../../hooks/useUserRole";
import { auth, db } from "../../lib/firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

function prettyDateFromIso(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function sortRequests(list) {
  return [...(list || [])].sort((a, b) => {
    const aApproved = a.status === "approved";
    const bApproved = b.status === "approved";

    if (aApproved !== bApproved) return aApproved ? 1 : -1;

    const aMs =
      a.createdAt?.toDate?.()?.getTime?.() ||
      Number(a.createdAtMs || 0) ||
      0;

    const bMs =
      b.createdAt?.toDate?.()?.getTime?.() ||
      Number(b.createdAtMs || 0) ||
      0;

    return bMs - aMs;
  });
}

function clonePlain(v) {
  return JSON.parse(JSON.stringify(v || {}));
}

export default function TimeOffRequests() {
  const { role } = useUserRole();
  const isSuperUser = isSuperRole(role);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");

  async function loadRequests() {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, "timeOffRequests"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRequests(sortRequests(rows));
    } catch (e) {
      console.error("Failed to load time off requests:", e);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRequests();
  }, []);

  async function handleApprove(req) {
    if (!isSuperUser || !req?.id) return;

    const busy = `approve_${req.id}`;
    setBusyKey(busy);

    try {
      await runTransaction(db, async (tx) => {
        const reqRef = doc(db, "timeOffRequests", req.id);
        const reqSnap = await tx.get(reqRef);

        if (!reqSnap.exists()) throw new Error("Request no longer exists.");

        const liveReq = { id: reqSnap.id, ...reqSnap.data() };

        if (liveReq.status !== "pending") {
          throw new Error("This request is no longer pending.");
        }

        const scheduleRef = doc(db, "schedules", liveReq.dateId);
        const scheduleSnap = await tx.get(scheduleRef);

        const day = scheduleSnap.exists() ? clonePlain(scheduleSnap.data()) : {};

        if (!day.__assignments || typeof day.__assignments !== "object") {
          day.__assignments = {};
        }

        if (!day.__availableShifts || typeof day.__availableShifts !== "object") {
          day.__availableShifts = {};
        }

        const roleKey = liveReq.roleKey;
        const list = Array.isArray(day.__assignments[roleKey])
          ? [...day.__assignments[roleKey]]
          : [];

        const nextList = list.filter((a) => {
          if (!a) return false;

          if (liveReq.assignmentId && a.assignmentId === liveReq.assignmentId) {
            return false;
          }

          if (
            String(a.employeeId || "") === String(liveReq.userId || "") &&
            String(a.start || "") === String(liveReq.start || "") &&
            String(a.end || "") === String(liveReq.end || "")
          ) {
            return false;
          }

          return true;
        });

        day.__assignments[roleKey] = nextList;
        day[roleKey] = nextList.map((a) => a.employeeId).filter(Boolean);

        if (!day.__blockedSlots || typeof day.__blockedSlots !== "object") {
          day.__blockedSlots = {};
        }

        if (nextList.length === 0 && day.__blockedSlots[roleKey]) {
          delete day.__blockedSlots[roleKey];
        }

        tx.set(scheduleRef, day, { merge: true });

        tx.update(reqRef, {
          status: "approved",
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          approvedByUid: auth.currentUser?.uid || "",
          approvedByName:
            auth.currentUser?.displayName ||
            auth.currentUser?.email ||
            "Super User",
        });
      });

      await loadRequests();
    } catch (e) {
      console.error("Failed to approve time off request:", e);
      alert(e?.message || "Could not approve request.");
    } finally {
      setBusyKey("");
    }
  }

  async function handleDelete(req) {
    if (!isSuperUser || !req?.id) return;

    const ok = window.confirm("Delete this time off request?");
    if (!ok) return;

    const busy = `delete_${req.id}`;
    setBusyKey(busy);

    try {
      await deleteDoc(doc(db, "timeOffRequests", req.id));
      await loadRequests();
    } catch (e) {
      console.error("Failed to delete time off request:", e);
      alert("Could not delete request.");
    } finally {
      setBusyKey("");
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <AdminPageHeader title="Time Off Requests" />

      <div style={{ color: "#64748b", marginTop: -8, marginBottom: 18 }}>
        Pending requests appear first. Approved requests remain below for tracking.
      </div>

      {loading ? (
        <div>Loading requests…</div>
      ) : requests.length === 0 ? (
        <div
          style={{
            padding: 18,
            borderRadius: 16,
            background: "#fff",
            border: "1px solid #e5e7eb",
          }}
        >
          No time off requests yet.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {requests.map((req) => {
            const approved = req.status === "approved";
            const pending = req.status === "pending";

            return (
              <div
                key={req.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 14,
                  alignItems: "center",
                  padding: 16,
                  borderRadius: 18,
                  background: approved ? "#f1f5f9" : "#ffffff",
                  border: "1px solid #e5e7eb",
                  opacity: approved ? 0.75 : 1,
                }}
              >
                <div>
                  <div style={{ fontWeight: 900, color: "#07133a" }}>
                    {req.userName || req.userEmail || "User"} •{" "}
                    {req.assignment || req.roleKey || "Shift"}
                  </div>

                  <div style={{ color: "#64748b", marginTop: 4, fontSize: 14 }}>
                    {prettyDateFromIso(req.dateId)} • {req.start || "—"} –{" "}
                    {req.end || "—"}
                    {req.userPosition ? ` • ${req.userPosition}` : ""}
                  </div>

                  <div style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
                    Status: <strong>{req.status || "pending"}</strong>
                    {approved && req.approvedByName
                      ? ` • Approved by ${req.approvedByName}`
                      : ""}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  {pending && isSuperUser && (
                    <button
                      className="btn"
                      disabled={busyKey === `approve_${req.id}`}
                      onClick={() => handleApprove(req)}
                    >
                      {busyKey === `approve_${req.id}` ? "Approving…" : "Approve"}
                    </button>
                  )}

                  {isSuperUser && (
                    <button
                      className="btn btn-secondary"
                      disabled={busyKey === `delete_${req.id}`}
                      onClick={() => handleDelete(req)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}